/**
 * 已知爬虫 / 外部抓取 UA 具名串（小写匹配）。
 * 原则：fail-open —— 只拦明确自报身份的爬虫；不确定一律放行。
 * 命中后由入口零请求早退（不建队列、不发任何上报）。
 * 与 react-plugin/src/utils/isKnownCrawlerUA.ts 保持同一名单。
 *
 * 来源含 2026-08-11 全平台爬虫明细（Meta / SnapchatAds / 搜索 / AI / SEO / headless）
 * 2026-08-13 补充：yeti / daumoa（韩日门户真实爬虫 token）/ screenshotservice /
 * semrush / dotbot / mj12bot
 *
 * 收录标准：只收"爬虫专属 token"。刻意不收被爬虫借用的真实用户 UA
 * （MicroMessenger / weixin / NAVER(inapp) / daumapps / NewsSapphire / panda）——
 * 拦它们等于拦社媒与 IM 渠道的真实流量，对换皮爬虫零效果；伪装爬虫由服务端行为打分识别。
 */
import storage from './storage';
import storageKeys from './storageKeys';

export var KNOWN_CRAWLER_UA_PATTERNS = [
  // 既有前端名单
  'bot.htm',
  'applebot',
  'storebot-google',
  'facebookexternalhit',
  'amazonproductbot',
  // 后端具名串
  'baiduspider',
  'bytespider',
  'meta-externalads',
  'meta-externalagent',
  'facebookscraper',
  'googleother',
  'googlebot-news',
  'googlebot',
  'python-urllib/2.5',
  'download demon/3.5.0.11',
  'uzbl (webkit 1.3)',
  // TikTok Ads 多图素材抓取（子串同时覆盖 TTAM_Multi_Image / TTAM_Multi_Images）
  'ttam_multi_image',
  // 社媒 / 广告预览抓取（高流量店铺明细）
  'meta-webindexer',
  'snapchatads',
  // 搜索引擎（搜狗用具名 spider 串，避免短串 sogou 误伤）
  'sogou web spider',
  'sogou pic spider',
  'sogou inst spider',
  'sogou head spider',
  'sogou news spider',
  'sogou orion spider',
  'sogou spider2',
  'yandexbot',
  'yandexrenderresourcesbot',
  'petalbot',
  // 韩日门户真实爬虫 token（注意不是 naver(inapp) / daumapps，后者是 App 内真实用户）
  'yeti',
  'daumoa',
  // AI 爬虫
  'claudebot',
  'gptbot',
  'chatgpt-user',
  'oai-searchbot',
  // SEO / 审计 / 支付预览 / 追踪探测 / headless
  // （semrush/dotbot/mj12bot 不执行 JS，前端为防御性冗余，用于对齐后端名单）
  'ahrefsbot',
  'ahrefssiteaudit',
  'semrush',
  'dotbot',
  'mj12bot',
  'stripebot',
  'tracking-quality-spider',
  // 店铺 328 观察到的截图 bot token（HeadlessChrome/151 ... ScreenshotService）；
  // 对方改名即失效，属过渡规则
  'screenshotservice',
  // 自家 e2e / sdk-verify 脚本均已自定义非 Headless UA，不受影响；
  // 新增 headless 脚本时需自定义 UA，勿直接依赖默认 Playwright/Puppeteer UA
  'headlesschrome',
  // OpenAI 广告爬虫（和 oai-searchbot 不同，是 AdsBot 系列）
  'oai-adsbot',
  // 数据里观察到的其他 AI/广告爬虫
  'amazonbot',        // Amazon 的 Alexa 爬虫
  'cohere-ai',        // Cohere AI 爬虫
  'ccbot',            // CommonCrawl AI 训练数据爬虫
  'diffbot',          // 知识图谱爬虫
  'imagesiftbot',     // 图片搜索爬虫
  'yisouspider',      // 神马搜索（UC/阿里旗下）的爬虫
];

/**
 * 是否命中已知爬虫 UA。
 * @param {string|null|undefined} userAgent 默认取 navigator.userAgent；空串返回 false（放行）
 * @returns {boolean}
 */
export default function isKnownCrawlerUA(userAgent) {
  var ua =
    userAgent != null
      ? userAgent
      : typeof navigator !== 'undefined'
        ? navigator.userAgent
        : '';
  if (!ua) {
    return false;
  }

  var uaLower = String(ua).toLowerCase();
  for (var i = 0; i < KNOWN_CRAWLER_UA_PATTERNS.length; i++) {
    if (uaLower.indexOf(KNOWN_CRAWLER_UA_PATTERNS[i]) !== -1) {
      return true;
    }
  }
  return false;
}

/** 从页面 script src 解析 carttrack 的 companyId（id=） */
export function getCarttrackCompanyIdFromPage() {
  try {
    // 优先从当前执行脚本取店铺 id，避免页面删除 script 标签后遍历不到
    var currentScriptSrc =
      (document.currentScript &&
        (document.currentScript.src ||
          (document.currentScript.getAttribute && document.currentScript.getAttribute('data-src')))) ||
      '';
    if (currentScriptSrc.indexOf('carttrack') !== -1) {
      var currentScriptMatched = currentScriptSrc.match(new RegExp('id=([^&]+)'));
      if (currentScriptMatched && currentScriptMatched[1]) {
        return currentScriptMatched[1];
      }
    }

    var scripts = document.getElementsByTagName('script');
    for (var i = 0; i < scripts.length; i++) {
      var el = scripts[i];
      var src = el.src || (el.getAttribute && el.getAttribute('data-src')) || '';
      if (!src || src.indexOf('carttrack') === -1) {
        continue;
      }
      var matched = src.match(new RegExp('id=([^&]+)'));
      if (matched && matched[1]) {
        return matched[1];
      }
    }
  } catch (e) {}
  try {
    return storage.getLocal(storageKeys.CARTX_COMPANY_ID) || '';
  } catch (e2) {
    return '';
  }
}

/**
 * 命中爬虫时的本地 debug 日志（与 carttrack c.log 同一开关：localStorage.debug 含 cartsee:）
 * 不发任何网络请求。
 */
export function logCrawlerHit(extra) {
  try {
    var debugFlag = storage.getLocal(storageKeys.DEBUG) || '';
    if (!String(debugFlag).includes('cartsee:')) {
      return;
    }
    var companyId = (extra && extra.companyId) || getCarttrackCompanyIdFromPage() || '';
    var url = (extra && extra.url) || (typeof location !== 'undefined' ? location.href : '');
    console.log('%ccartsee:crawler', 'color: #513EFF', '命中爬虫', {
      companyId: companyId,
      url: url
    });
  } catch (e) {}
}
